/**
 * EditProfileForm — partial customer profile update with OTP protection.
 *
 * Fields:
 *   • Personal: full_name, email, mobile_e164, dob
 *   • Branch questionnaire: rendered via DynamicField from
 *     GET /profile/questionnaire-config
 *
 * Behaviour:
 *   • On submit, compute a delta vs the current profile and PATCH only
 *     changed fields.
 *   • Backend decides whether an OTP challenge is required. If yes, the
 *     useOtpProtectedProfileUpdate hook holds the payload and the dialog
 *     opens; on verify, the same payload re-sends with `otp`.
 */
import * as React from 'react';
import { useForm, type Control, type UseFormRegister, type UseFormWatch } from 'react-hook-form';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, toast } from '@finmin/ui';
import { Loader2 } from 'lucide-react';
import type { CustomerProfileResponse, ProfileUpdateRequest } from '@/api/profile';
import { useQuestionnaireConfig } from '@/features/questionnaire/hooks/use-questionnaire-config';
import { DynamicField } from '@/features/questionnaire/components/dynamic-field';
import type { QuestionnaireInput } from '@/features/questionnaire/schema';
import type { CustomerTypeKey } from '@/features/questionnaire/types';
import { useOtpProtectedProfileUpdate } from './hooks/use-otp-protected-profile-update';
import { OtpChallengeDialog } from './components/otp-challenge-dialog';

interface EditProfileFormProps {
  profile:    CustomerProfileResponse;
  onCancel:   () => void;
  onSuccess?: () => void;
}

// Build the RHF defaults from the current profile.
function defaultsFromProfile(profile: CustomerProfileResponse): Partial<QuestionnaireInput & {
  full_name?: string;
  email?:     string;
  mobile_e164?: string;
  dob?:       string;
}> {
  return {
    full_name:                      (profile.full_name ?? '') as string,
    email:                          (profile.email ?? '') as string,
    mobile_e164:                    (profile.mobile_e164 ?? '') as string,
    dob:                            (profile.dob ?? '') as string,
    customer_type:                  (profile.customer_type ?? undefined) as CustomerTypeKey | undefined,
    annual_income_range:            (profile.annual_income_range ?? undefined) as never,
    annual_turnover_range:          (profile.annual_turnover_range ?? undefined) as never,
    entity_structure:               (profile.entity_structure ?? undefined) as never,
    entity_structure_other_detail:  (profile.entity_structure_other_detail ?? '') as string,
    income_exposures:               (profile.income_exposures ?? []) as never,
    other_exposure_detail:          (profile.other_exposure_detail ?? '') as string,
    services_required:              (profile.services_required ?? []) as never,
    service_other_detail:           (profile.service_other_detail ?? '') as string,
    other_customer_category:        (profile.other_customer_category ?? undefined) as never,
    other_customer_category_detail: (profile.other_customer_category_detail ?? '') as string,
  };
}

type EditFormValues = ReturnType<typeof defaultsFromProfile>;

// Decide if a value is "set" (used for delta diffing).
function setEq(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
  }
  if ((a == null || a === '') && (b == null || b === '')) return true;
  return a === b;
}

// Compute changed fields vs the saved profile.
function buildPayload(values: EditFormValues, profile: CustomerProfileResponse): ProfileUpdateRequest {
  const out: ProfileUpdateRequest = {};
  const keys: (keyof EditFormValues)[] = [
    'full_name', 'email', 'mobile_e164', 'dob',
    'customer_type',
    'annual_income_range', 'annual_turnover_range',
    'entity_structure', 'entity_structure_other_detail',
    'income_exposures', 'other_exposure_detail',
    'services_required', 'service_other_detail',
    'other_customer_category', 'other_customer_category_detail',
  ];

  for (const key of keys) {
    const next = values[key];
    const prev = (profile as Record<string, unknown>)[key as string];

    if (setEq(next, prev)) continue;

    // Trim text fields.
    if (typeof next === 'string') {
      const trimmed = next.trim();
      (out as Record<string, unknown>)[key] = trimmed === '' ? null : trimmed;
      continue;
    }

    (out as Record<string, unknown>)[key] = next as never;
  }
  return out;
}

export function EditProfileForm({ profile, onCancel, onSuccess }: EditProfileFormProps) {
  const {
    data: config,
    isLoading: configLoading,
  } = useQuestionnaireConfig();

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<EditFormValues>({
    defaultValues: defaultsFromProfile(profile),
    mode: 'onSubmit',
  });

  const otp = useOtpProtectedProfileUpdate({
    onUpdated: () => {
      toast.success('Profile updated.');
      onSuccess?.();
    },
  });

  const customerType = watch('customer_type') as CustomerTypeKey | undefined;
  const activeBranch = config && customerType
    ? config.branches.find((b) => b.customer_type === customerType) ?? null
    : null;

  async function onSubmit(values: EditFormValues) {
    const payload = buildPayload(values, profile);

    if (Object.keys(payload).length === 0) {
      toast.warning('Nothing to update.');
      onCancel();
      return;
    }

    try {
      await otp.submit(payload);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed. Please try again.';
      toast.error(msg);
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        {/* ── Personal Information ──────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Full name" error={errors.full_name?.message as string | undefined}>
              <Input {...register('full_name')} placeholder="Your full name" />
            </Field>
            <Field label="Email" error={errors.email?.message as string | undefined}>
              <Input type="email" {...register('email')} placeholder="you@example.com" />
            </Field>
            <Field label="Mobile (E.164)" error={errors.mobile_e164?.message as string | undefined}>
              <Input {...register('mobile_e164')} placeholder="+91…" />
            </Field>
            <Field label="Date of birth" error={errors.dob?.message as string | undefined}>
              <Input type="date" {...register('dob')} />
            </Field>
          </CardContent>
        </Card>

        {/* ── Financial Profile (config-driven) ─────────────────────────── */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Financial Profile</CardTitle>
          </CardHeader>
          <CardContent>
            {configLoading || !config ? (
              <div className="py-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading questionnaire…
              </div>
            ) : !activeBranch ? (
              <p className="text-sm text-muted-foreground">
                Customer type isn't set — finish onboarding first.
              </p>
            ) : (
              <div className="space-y-5">
                {activeBranch.fields.map((field) => (
                  <DynamicField
                    key={field.name}
                    field={field}
                    // EditFormValues is a superset of QuestionnaireInput
                    // (adds full_name/email/mobile_e164). The RHF generics
                    // don't subtype, so we cast — runtime shapes match.
                    control={control as unknown as Control<QuestionnaireInput>}
                    register={register as unknown as UseFormRegister<QuestionnaireInput>}
                    watch={watch as unknown as UseFormWatch<QuestionnaireInput>}
                    errors={errors as never}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Action row ────────────────────────────────────────────────── */}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={otp.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={otp.isPending}>
            {otp.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
              </span>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </form>

      <OtpChallengeDialog
        open={otp.phase !== 'idle'}
        otpTarget={otp.challenge?.otp_target ?? null}
        expiresInSec={otp.challenge?.exp ?? 300}
        isVerifying={otp.phase === 'verifying'}
        errorMessage={otp.error?.message ?? null}
        onSubmit={async (code) => {
          try {
            await otp.verify(code);
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Verification failed.';
            toast.error(msg);
          }
        }}
        onCancel={otp.cancel}
      />
    </>
  );
}

// ── Small layout helper ─────────────────────────────────────────────────────

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
